'use client'

import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { X, Check, SlidersHorizontal } from 'lucide-react'
import useCountries from '@/hooks/cachedCalls/use-countries'
import useProvinces from '@/hooks/cachedCalls/use-states'
import useSports from '@/hooks/cachedCalls/use-sports'
import { Metadata } from '@/types/metadata'
import { Deporte } from '@/types/category'
import { useToast } from '@/hooks/use-toast'
import { useUserProfile } from '@/hooks/use-user'
import { usePreferencesModal } from '@/context/PreferencesModalContext'
import useEventCountries from '@/hooks/cachedCalls/use-event-countries'

interface PreferencesModalProps {
  dict: any
  lang: string
}

interface SportCategory {
  sportId: number
  categoryIds: number[]
}

export default function PreferencesModal({ dict, lang }: PreferencesModalProps) {
  const { toast } = useToast()
  const { userProfile, userPreferences, updateUserPreferences, preferencesLoading } = useUserProfile()
  const { showModal, closeModal } = usePreferencesModal()
  
  const [selectedCountry, setSelectedCountry] = useState<string>('')
  const [selectedProvinces, setSelectedProvinces] = useState<Metadata[]>([])
  const [provinceSearch, setProvinceSearch] = useState('')
  const [isProvinceSearchFocused, setIsProvinceSearchFocused] = useState(false)
  const [selectedSports, setSelectedSports] = useState<number[]>([])
  const [sportCategories, setSportCategories] = useState<SportCategory[]>([])
  const [sportSearch, setSportSearch] = useState('')
  const [isSportSearchFocused, setIsSportSearchFocused] = useState(false)
  const [displayedSports, setDisplayedSports] = useState<number[]>([7, 21, 99, 104]) // Running, Ciclismo, Triathlon, Trail
  const [isSaving, setIsSaving] = useState(false)
  const [provincesInitiallyLoaded, setProvincesInitiallyLoaded] = useState(false)

  const { data: countries } = useEventCountries()
  const { data: provinces, loading: loadingProvinces } = useProvinces(lang, selectedCountry)
  const { data: sportsData } = useSports([lang])
  const sports = sportsData[0] || []

  // Get locale for sorting countries alphabetically
  const getLocaleForSorting = (language: string): string => {
    const localeMap: Record<string, string> = {
      'es': 'es-ES',
      'en': 'en-GB',
      'pt': 'pt-BR',
      'ca': 'ca-ES',
      'eu': 'eu-ES',
      'fr': 'fr-FR',
      'it': 'it-IT'
    }
    return localeMap[language] || 'en-GB'
  }

  // Sort countries alphabetically by the selected language
  const sortedCountries = [...(countries || [])].sort((a, b) => {
    return a.value.localeCompare(b.value, getLocaleForSorting(lang), { sensitivity: 'base' })
  })

  useEffect(() => {
    if (userProfile?.id && userPreferences) {
      try {
        if (userPreferences.countryId) setSelectedCountry(userPreferences.countryId)
        if (userPreferences.sports && Array.isArray(userPreferences.sports)) {
          const sportIds = userPreferences.sports.map((s: any) => s.sportId)
          setSelectedSports(sportIds)
          setSportCategories(userPreferences.sports.map((s: any) => ({
            sportId: s.sportId,
            categoryIds: s.categoryIds
          })))
          
          const defaultSports = [7, 21, 99, 104]
          const allDisplayedSports = [...new Set([...defaultSports, ...sportIds])]
          setDisplayedSports(allDisplayedSports)
        }
      } catch (error) {
        console.error('Error loading preferences:', error)
      }
    }
  }, [userProfile?.id, userPreferences])

  // Map stored province IDs to Metadata objects when provinces are loaded
  useEffect(() => {
    if (userProfile?.id && !loadingProvinces && provinces.length > 0 && !provincesInitiallyLoaded && userPreferences) {
      try {
        if (userPreferences.provinceIds && Array.isArray(userPreferences.provinceIds)) {
          const mappedProvinces = provinces.filter(p => userPreferences.provinceIds!.includes(p.id))
          setSelectedProvinces(mappedProvinces)
        }
        setProvincesInitiallyLoaded(true)
      } catch (error) {
        console.error('Error mapping provinces:', error)
        setProvincesInitiallyLoaded(true)
      }
    }
  }, [userProfile?.id, loadingProvinces, provinces, provincesInitiallyLoaded, userPreferences])

  const filteredProvinces = provinces.filter(province =>
    province.value.toLowerCase().includes(provinceSearch.toLowerCase())
  )

  const filteredSports = sportSearch
    ? sports.filter((sport: Deporte) =>
        sport.nombre.toLowerCase().includes(sportSearch.toLowerCase()) &&
        !displayedSports.includes(sport.idDeporte)
      )
    : sports.filter((sport: Deporte) => !displayedSports.includes(sport.idDeporte))
  
  const sportsToDisplay = sports.filter((sport: Deporte) => 
    displayedSports.includes(sport.idDeporte)
  )

  const toggleSport = (sportId: number) => {
    if (selectedSports.includes(sportId)) {
      setSelectedSports(selectedSports.filter(id => id !== sportId))
      setSportCategories(sportCategories.filter(cat => cat.sportId !== sportId))
    } else {
      setSelectedSports([...selectedSports, sportId])
      setSportCategories([...sportCategories, { sportId, categoryIds: [] }])
    }
  }

  const addSportToDisplay = (sportId: number) => {
    if (!displayedSports.includes(sportId)) {
      setDisplayedSports([...displayedSports, sportId])
    }
    if (!selectedSports.includes(sportId)) {
      setSelectedSports([...selectedSports, sportId])
      setSportCategories([...sportCategories, { sportId, categoryIds: [] }])
    }
    setSportSearch('')
    setIsSportSearchFocused(false)
  }

  const toggleCategory = (sportId: number, categoryId: number) => {
    setSportCategories(prev => {
      const sportCat = prev.find(cat => cat.sportId === sportId)
      if (!sportCat) return prev

      const hasCategory = sportCat.categoryIds.includes(categoryId)
      return prev.map(cat => {
        if (cat.sportId === sportId) {
          return {
            ...cat,
            categoryIds: hasCategory
              ? cat.categoryIds.filter(id => id !== categoryId)
              : [...cat.categoryIds, categoryId]
          }
        }
        return cat
      })
    })
  }

  const addProvince = (province: Metadata) => {
    if (!selectedProvinces.find(p => p.id === province.id)) {
      setSelectedProvinces([...selectedProvinces, province])
    }
    setProvinceSearch('')
    setIsProvinceSearchFocused(false)
  }

  const removeProvince = (provinceId: number) => {
    setSelectedProvinces(selectedProvinces.filter(p => p.id !== provinceId))
  }

  const selectAllProvinces = () => {
    setSelectedProvinces([])
    setProvinceSearch('')
    setIsProvinceSearchFocused(false)
  }

  const handleSave = async () => {
    if (!userProfile?.id) {
      return
    }

    setIsSaving(true)
    try {
      const preferences = {
        countryId: selectedCountry,
        provinceIds: selectedProvinces.map(p => p.id),
        sports: sportCategories,
      }
      
      const success = await updateUserPreferences(preferences)
      
      if (success) {
        toast({
          title: dict.dashboard.success,
          description: dict.dashboard.preferences.save_success,
        })
        closeModal()
      } else {
        toast({
          title: dict.dashboard.preferences.error,
          description: dict.dashboard.preferences.save_error,
          variant: 'destructive',
        })
      }
    } catch (error) {
      console.error('Error saving preferences:', error)
      toast({
        title: dict.dashboard.preferences.error,
        description: dict.dashboard.preferences.save_error,
        variant: 'destructive',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handlePostpone = () => {
    closeModal()
  }

  if (!showModal) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={handlePostpone} />
      
      <div className="relative bg-background rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-background border-b border-border p-6 flex items-start justify-between gap-4">
          <div className="flex gap-4 items-center">
            <div className="hidden sm:flex flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 items-center justify-center">
              <SlidersHorizontal className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-semibold text-foreground">
                {dict.dashboard.preferences.personalize_title}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {dict.dashboard.preferences.personalize_description}
              </p>
            </div>
          </div>
          <button
            onClick={handlePostpone}
            className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="space-y-3">
            <Label htmlFor="modal-country" className="text-base font-medium">
              {dict.dashboard.preferences.country_label}
            </Label>
            <Select value={selectedCountry} onValueChange={setSelectedCountry}>
              <SelectTrigger id="modal-country" className="w-full">
                <SelectValue placeholder={dict.dashboard.preferences.select_country} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {sortedCountries.map((country) => (
                  <SelectItem key={country.id} value={country.id.toString()}>
                    {country.value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedCountry && selectedCountry !== 'all' && (
            <div className="space-y-3">
              <Label htmlFor="modal-provinces" className="text-base font-medium">
                {dict.dashboard.preferences.provinces_label}
              </Label>
              
              <div className="flex flex-wrap gap-2 mb-2">
                {selectedProvinces.length === 0 ? (
                  <div className="inline-flex items-center gap-1 px-3 py-1 bg-primary text-primary-foreground rounded-full text-sm">
                    Todas
                  </div>
                ) : (
                  selectedProvinces.map((province) => (
                    <div
                      key={province.id}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-primary text-primary-foreground rounded-full text-sm"
                    >
                      {province.value}
                      <button
                        onClick={() => removeProvince(province.id)}
                        className="ml-1 hover:bg-primary/90 rounded-full p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))
                )}
              </div>

              <div className="relative">
                <Input
                  id="modal-provinces"
                  type="text"
                  placeholder={dict.dashboard.preferences.search_province_placeholder}
                  value={provinceSearch}
                  onChange={(e) => setProvinceSearch(e.target.value)}
                  onFocus={() => setIsProvinceSearchFocused(true)}
                  onBlur={() => setTimeout(() => setIsProvinceSearchFocused(false), 200)}
                  className="w-full"
                />
                
                {isProvinceSearchFocused && (
                  <div className="absolute z-10 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-auto">
                    {selectedProvinces.length > 0 && (
                      <button
                        onClick={selectAllProvinces}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors border-b border-border font-medium"
                      >
                        Todas
                      </button>
                    )}
                    {filteredProvinces
                      .filter(province => !selectedProvinces.find(p => p.id === province.id))
                      .map((province) => (
                        <button
                          key={province.id}
                          onClick={() => addProvince(province)}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
                        >
                          {province.value}
                        </button>
                      ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="space-y-4">
            <Label className="text-base font-medium">
              {dict.dashboard.preferences.sports_label}
            </Label>

            <div className="flex flex-wrap gap-2">
              {sportsToDisplay.map((sport: Deporte) => {
                const isSelected = selectedSports.includes(sport.idDeporte)
                return (
                  <button
                    key={sport.idDeporte}
                    onClick={() => toggleSport(sport.idDeporte)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      isSelected
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-background border border-input hover:bg-accent'
                    }`}
                  >
                    {isSelected && <Check className="inline-block h-4 w-4 mr-1" />}
                    {sport.nombre}
                  </button>
                )
              })}
            </div>

            {selectedSports.map((sportId) => {
              const sport = sports.find((s: Deporte) => s.idDeporte === sportId)
              if (!sport || !sport.subDeportes || sport.subDeportes.length === 0) return null

              const sportCat = sportCategories.find(cat => cat.sportId === sportId)

              return (
                <div key={sportId} className="pl-4 space-y-3 border-l-2 border-border">
                  <Label className="text-sm font-medium text-muted-foreground">
                    {dict.dashboard.preferences.categories_of} {sport.nombre}
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {sport.subDeportes.map((subSport) => {
                      const isSelected = sportCat?.categoryIds.includes(subSport.idSubDeporte)
                      return (
                        <button
                          key={subSport.idSubDeporte}
                          onClick={() => toggleCategory(sportId, subSport.idSubDeporte)}
                          className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                            isSelected
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-background border border-input hover:bg-accent'
                          }`}
                        >
                          {isSelected && <Check className="inline-block h-3 w-3 mr-1" />}
                          {subSport.nombre}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )
            })}

            <div className="space-y-2">
              <div className="relative">
                <Input
                  type="text"
                  placeholder={dict.dashboard.preferences.other_sport_placeholder}
                  value={sportSearch}
                  onChange={(e) => setSportSearch(e.target.value)}
                  onFocus={() => setIsSportSearchFocused(true)}
                  onBlur={() => setTimeout(() => setIsSportSearchFocused(false), 200)}
                  className="w-full"
                />
                
                {isSportSearchFocused && filteredSports.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-auto">
                    {filteredSports.map((sport: Deporte) => (
                      <button
                        key={sport.idDeporte}
                        onClick={() => addSportToDisplay(sport.idDeporte)}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
                      >
                        {sport.nombre}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-background border-t border-border p-6 flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={handlePostpone}
            disabled={isSaving}
          >
            {dict.dashboard.preferences.postpone}
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? dict.dashboard.preferences.saving : dict.dashboard.preferences.save}
          </Button>
        </div>
      </div>
    </div>
  )
}

